import { type Query, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetBatchStatusV1ImagesImageIdGeneratePromptsBatchIdGetData,
  type GetBatchStatusV1ImagesImageIdGeneratePromptsBatchIdGetError,
  type GetBatchStatusV1ImagesImageIdGeneratePromptsBatchIdGetResponse,
  getBatchStatusV1ImagesImageIdGeneratePromptsBatchIdGet,
} from '../../generated';

export type GetBatchStatusQueryVariables = GetBatchStatusV1ImagesImageIdGeneratePromptsBatchIdGetData['path'];

export type GetBatchStatusQuery = GetBatchStatusV1ImagesImageIdGeneratePromptsBatchIdGetResponse;

export const useGetBatchStatusQuery = <
  TData = GetBatchStatusQuery,
  TError = GetBatchStatusV1ImagesImageIdGeneratePromptsBatchIdGetError,
>(
  { image_id, batch_id }: GetBatchStatusQueryVariables,
  options?: Omit<UseQueryOptions<GetBatchStatusQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetBatchStatusQuery, TError, TData>({
    queryKey: ['GetBatchStatus', { image_id, batch_id }],
    queryFn: async () => {
      const { data } = await getBatchStatusV1ImagesImageIdGeneratePromptsBatchIdGet({
        path: { image_id, batch_id },
        throwOnError: true,
      });
      return data;
    },
    refetchInterval: (query: Query<GetBatchStatusQuery, TError>) => {
      const status = query.state.data?.status;

      if (status === 'PROCESSING') {
        return 10000; // Poll every 10 seconds
      }

      return false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });
};

useGetBatchStatusQuery.getKey = ({ image_id, batch_id }: GetBatchStatusQueryVariables) => [
  'GetBatchStatus',
  { image_id, batch_id },
];

useGetBatchStatusQuery.fetcher = (
  { image_id, batch_id }: GetBatchStatusQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getBatchStatusV1ImagesImageIdGeneratePromptsBatchIdGet({
      path: { image_id, batch_id },
      throwOnError: true,
    });
    return data;
  };
};
